import type { WorkstreamDto } from '@project-os/contracts';
import type {
  CreateWorkstreamInput,
  ReorderWorkstreamsInput,
  UpdateWorkstreamInput,
} from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';

export const workstreamKeys = {
  list: (projectId: string) => ['projects', projectId, 'workstreams'] as const,
};

export function useWorkstreams(projectId: string | undefined) {
  return useQuery({
    queryKey: workstreamKeys.list(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<WorkstreamDto[]>(`/projects/${projectId}/workstreams`, { signal }),
    enabled: Boolean(projectId),
    staleTime: 60_000,
  });
}

/** Все изменения возвращают новый список — кэш обновляется ответом. */
function useWorkstreamMutation<TInput>(
  projectId: string,
  request: (input: TInput) => Promise<WorkstreamDto[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (workstreams) => {
      queryClient.setQueryData(workstreamKeys.list(projectId), workstreams);
    },
  });
}

export function useCreateWorkstream(projectId: string) {
  return useWorkstreamMutation(projectId, (input: CreateWorkstreamInput) =>
    apiRequest<WorkstreamDto[]>(`/projects/${projectId}/workstreams`, {
      method: 'POST',
      body: input,
    }),
  );
}

export function useUpdateWorkstream(projectId: string) {
  return useWorkstreamMutation(
    projectId,
    (input: UpdateWorkstreamInput & { workstreamId: string }) => {
      const { workstreamId, ...body } = input;
      return apiRequest<WorkstreamDto[]>(`/projects/${projectId}/workstreams/${workstreamId}`, {
        method: 'PATCH',
        body,
      });
    },
  );
}

export function useArchiveWorkstream(projectId: string) {
  return useWorkstreamMutation(projectId, (workstreamId: string) =>
    apiRequest<WorkstreamDto[]>(`/projects/${projectId}/workstreams/${workstreamId}`, {
      method: 'DELETE',
    }),
  );
}

export function useReorderWorkstreams(projectId: string) {
  return useWorkstreamMutation(projectId, (input: ReorderWorkstreamsInput) =>
    apiRequest<WorkstreamDto[]>(`/projects/${projectId}/workstreams/order`, {
      method: 'PATCH',
      body: input,
    }),
  );
}
